import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  ShieldCheck,
  Database,
  RefreshCw,
  Zap,
  Search,
  Link2,
  Cog,
  Cloud,
  Code2,
  Lock,
  BarChart3,
  ClipboardList,
  PieChart,
  Box,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";
import StackNavbar from "../components/StackNavbar";
import Footer from "../components/Footer";
import { openAgent } from "../utils/openAgent";

const TESTIMONIALS = [
  {
    quote:
      "Migrating 15+ years of complex on-premise ERP records to the cloud with zero downtime seemed impossible until Simpel executed it with 100% data integrity.",
    name: "Sarah Jenkins",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Head of IT & Infrastructure • Apex Dynamics",
  },
  {
    quote:
      "Simpel's Legacy Data Migration solution helped us streamline our operations and modernize our data stack. The team's expertise and support have been invaluable.",
    name: "Tejas Karwa",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Project Manager • Aptia UK",
  },
  {
    quote:
      "The automated reconciliation and data cleansing delivered immediate analytics-readiness, cutting our reporting latency by over 70%.",
    name: "David Chen",
    image: "/images/testimonials/tejas-karwa.jpg",
    role: "Head of Digital Transformation • Enterprise Systems",
  },
];

const APPROACH_STEPS = [
  {
    number: "01",
    badgeGradient: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
    icon: Search,
    iconColor: "#2563EB",
    iconBg: "#EFF6FF",
    title: "Discover & Analyze",
    desc: "Assess your systems, data structures, and business requirements in granular detail.",
  },
  {
    number: "02",
    badgeGradient: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
    icon: Box,
    iconColor: "#9333EA",
    iconBg: "#FAF5FF",
    title: "Plan & Prepare",
    desc: "Define roadmap, cleanse and transform schema, and set up landing environments.",
  },
  {
    number: "03",
    badgeGradient: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
    icon: BarChart3,
    iconColor: "#16A34A",
    iconBg: "#F0FDF4",
    title: "Migrate & Validate",
    desc: "Execute phased migration and verify total data accuracy, schema parity, and compliance.",
  },
  {
    number: "04",
    badgeGradient: "linear-gradient(90deg, #00D2EE 0%, #2563EB 100%)",
    icon: BarChart3,
    iconColor: "#CA8A04",
    iconBg: "#FEFCE8",
    title: "Optimize & Support",
    desc: "Enable analytics, automated alerts, governance, and SLA-backed ongoing evolution.",
  },
];

const SERVICE_CAPABILITIES = [
  {
    icon: Database,
    iconBg: "#E0F2FE",
    iconColor: "#2563EB",
    title: "Data Assessment",
    desc: "Analyze existing legacy systems and data repositories to formulate an optimized, risk-free migration strategy.",
  },
  {
    icon: Cog,
    iconBg: "#FFEDD5",
    iconColor: "#EA580C",
    title: "Data Cleansing & Transformation",
    desc: "Standardize, validate, deduplicate, and enrich data structures for maximum accuracy in modern clouds.",
  },
  {
    icon: Cloud,
    iconBg: "#DCFCE7",
    iconColor: "#16A34A",
    title: "Platform Migration",
    desc: "Seamlessly transfer data workloads to modern cloud ERPs, warehouses, or custom enterprise platforms securely.",
  },
  {
    icon: Code2,
    iconBg: "#F3E8FF",
    iconColor: "#9333EA",
    title: "Application Integration",
    desc: "Ensure seamless interoperability with internal tools, connected services, and legacy business logic.",
  },
  {
    icon: Lock,
    iconBg: "#FCE7F3",
    iconColor: "#DB2777",
    title: "Data Security & Compliance",
    desc: "Maintain strict data governance, end-to-end encryption, and regulatory compliance throughout transit.",
  },
  {
    icon: BarChart3,
    iconBg: "#FEF9C3",
    iconColor: "#CA8A04",
    title: "Post-Migration Support",
    desc: "Continuous performance monitoring, query tuning, and SLA-backed support to guarantee sustainable success.",
  },
];

const MIGRATION_STEPS = [
  {
    step: 1,
    title: "Assess",
    desc: "Evaluate systems and data",
    icon: Search,
    iconColor: "#0284C7",
    bgColor: "#E0F2FE",
    glowColor: "rgba(0, 210, 238, 0.45)",
  },
  {
    step: 2,
    title: "Plan",
    desc: "Define migration strategy",
    icon: ClipboardList,
    iconColor: "#A855F7",
    bgColor: "#F3E8FF",
    glowColor: "rgba(168, 85, 247, 0.45)",
  },
  {
    step: 3,
    title: "Migrate",
    desc: "Execute data transfer & transformation",
    icon: Database,
    iconColor: "#CA8A04",
    bgColor: "#FEF9C3",
    glowColor: "rgba(202, 138, 4, 0.45)",
  },
  {
    step: 4,
    title: "Validate",
    desc: "Ensure data accuracy and integrity",
    icon: ShieldCheck,
    iconColor: "#16A34A",
    bgColor: "#DCFCE7",
    glowColor: "rgba(22, 163, 74, 0.45)",
  },
  {
    step: 5,
    title: "Optimize",
    desc: "Enable insights and continuous improvement",
    icon: BarChart3,
    iconColor: "#E11D48",
    bgColor: "#FFE4E6",
    glowColor: "rgba(225, 29, 72, 0.45)",
  },
];

const LegacyDataMigrationPage = () => {
  const navigate = useNavigate();
  const [activeTestimonial, setActiveTestimonial] = useState(1);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const handlePrevTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === 0 ? TESTIMONIALS.length - 1 : prev - 1
    );
  };

  const handleNextTestimonial = () => {
    setActiveTestimonial((prev) =>
      prev === TESTIMONIALS.length - 1 ? 0 : prev + 1
    );
  };

  const handleTalkToTeam = () => {
    openAgent("Legacy Data Migration");
  };

  return (
    <div className="min-h-screen w-full bg-[#010710] text-white flex flex-col selection:bg-[#00D2EE] selection:text-black overflow-x-hidden">
      {/* Top Navbar */}
      <StackNavbar />

      <main className="flex-1">
        {/* Hero Section matching Screenshot 1 exactly */}
        <section className="relative w-full overflow-hidden bg-[#010710] pt-[76px] sm:pt-[80px] min-h-[520px] sm:min-h-[560px] lg:min-h-[600px] xl:min-h-[620px] flex items-center">
          {/* Background Illustration spanning the full section, pinned to the right matching mockup */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none z-0">
            <img
              src="/images/services/legacy-data-migration-hero.png"
              alt="Move Your Data Forward. Without Leaving Value Behind."
              className="w-full h-full object-cover object-right pointer-events-none select-none block"
            />
          </div>

          {/* Subtle fade overlay on mobile/tablet screens for text legibility */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#010710] via-[#010710]/90 to-transparent lg:hidden pointer-events-none z-[1]" />

          {/* Section Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-12 lg:py-16 flex items-center">
            <div className="max-w-[580px] xl:max-w-[640px] flex flex-col items-start">
              {/* Breadcrumb matching mockup: WHAT WE DO > SERVICES > LEGACY DATA MIGRATION */}
              <div className="flex items-center gap-2 text-[10.5px] sm:text-[11.5px] font-bold uppercase tracking-[2.4px] text-[#7E93AC] select-none">
                <Link
                  to="/what-we-do"
                  className="hover:text-white transition-colors"
                >
                  WHAT WE DO
                </Link>
                <span>&gt;</span>
                <Link
                  to="/what-we-do#services"
                  className="hover:text-white transition-colors"
                >
                  SERVICES
                </Link>
                <span>&gt;</span>
                <span className="text-[#A2B6CE]">LEGACY DATA MIGRATION</span>
              </div>

              {/* Main Heading: 3 lines */}
              <h1 className="mt-5 text-[32px] sm:text-[38px] lg:text-[44px] xl:text-[48px] font-extrabold leading-[1.14] tracking-tight text-white">
                <span className="block sm:whitespace-nowrap">Move Your Data Forward.</span>
                <span className="block mt-1 sm:whitespace-nowrap">
                  <span>Without </span>
                  <span className="text-[#00D2EE]">Leaving </span>
                  <span className="text-[#2563EB]">Value</span>
                </span>
                <span className="block mt-1 text-[#00D2EE]">Behind.</span>
              </h1>

              {/* Subtitle / Description */}
              <p className="mt-4 sm:mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[26px] text-[#9FB3C8] max-w-[460px] xl:max-w-[490px]">
                Migrate, transform, and unlock the full potential of your legacy data
                — securely, accurately, and with minimal business disruption.
              </p>

              {/* CTA Button matching Screenshot 1 */}
              <div className="mt-7 sm:mt-8">
                <button
                  onClick={handleTalkToTeam}
                  className="group inline-flex items-center gap-2.5 rounded-full bg-white px-7 py-3.5 text-[14px] sm:text-[14.5px] font-bold text-[#0B132B] shadow-[0_0_28px_rgba(0,180,255,0.45)] transition-all duration-300 hover:scale-[1.03] hover:shadow-[0_0_40px_rgba(0,210,238,0.65)] active:scale-95 cursor-pointer"
                >
                  <span>Talk to our team</span>
                  <ArrowRight
                    className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                    strokeWidth={2.2}
                  />
                </button>
              </div>
            </div>
          </div>
        </section>

        {/* Seamless Transition Section matching Screenshot */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-28"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10 xl:gap-14">
              {/* Left Text Block */}
              <div className="max-w-[500px] xl:max-w-[540px] shrink-0">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                  SEAMLESS TRANSITION
                </p>
                <h2 className="mt-4 text-[30px] sm:text-[36px] lg:text-[38px] xl:text-[42px] font-extrabold text-[#0F172A] leading-[1.16] tracking-tight">
                  <span className="block sm:whitespace-nowrap">Your legacy data deserve a</span>
                  <span className="block text-[#00D2EE] sm:whitespace-nowrap">modern future.</span>
                </h2>
                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#64748B]">
                  Simpel's Legacy Data Migration services help you move data from outdated systems to modern, scalable platforms — ensuring data accuracy, security, and business continuity.
                </p>
              </div>

              {/* Right 3 Cards Grid */}
              <div className="flex-1 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 xl:gap-6">
                {/* Card 1: Ensure Data Accuracy */}
                <div className="rounded-[22px] bg-white p-7 xl:p-8 border border-slate-100 shadow-[0_16px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_22px_50px_rgba(15,23,42,0.1)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-start">
                  <div className="w-12 h-12 rounded-full bg-[#E0F2FE] flex items-center justify-center mb-6">
                    <Search className="w-5 h-5 text-[#0284C7]" strokeWidth={2.4} />
                  </div>
                  <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                    Ensure Data<br />Accuracy
                  </h3>
                  <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[20px] text-[#64748B]">
                    Validate and cleanse data to maintain integrity and reliability.
                  </p>
                </div>

                {/* Card 2: Minimize Business Disruption */}
                <div className="rounded-[22px] bg-white p-7 xl:p-8 border border-slate-100 shadow-[0_16px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_22px_50px_rgba(15,23,42,0.1)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-start">
                  <div className="w-12 h-12 rounded-full bg-[#F3E8FF] flex items-center justify-center mb-6">
                    <Link2 className="w-5 h-5 text-[#9333EA]" strokeWidth={2.4} />
                  </div>
                  <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                    Minimize Business<br />Disruption
                  </h3>
                  <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[20px] text-[#64748B]">
                    Plan and execute migration with minimal downtime.
                  </p>
                </div>

                {/* Card 3: Unlock Greater Value */}
                <div className="rounded-[22px] bg-white p-7 xl:p-8 border border-slate-100 shadow-[0_16px_40px_rgba(15,23,42,0.06)] hover:shadow-[0_22px_50px_rgba(15,23,42,0.1)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-start">
                  <div className="w-12 h-12 rounded-full bg-[#DCFCE7] flex items-center justify-center mb-6">
                    <Zap className="w-5 h-5 text-[#16A34A]" strokeWidth={2.4} />
                  </div>
                  <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                    Unlock Greater<br />Value
                  </h3>
                  <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[20px] text-[#64748B]">
                    Make your data accessible for analytics, automation, and future growth.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Service Capabilities Section matching Screenshot */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(236, 245, 253, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header: Title on Left, Divider + Description on Right */}
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-6 lg:gap-10 pb-12 sm:pb-14">
              <div>
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                  SERVICE CAPABILITIES
                </p>
                <h2 className="mt-3 text-[30px] sm:text-[36px] lg:text-[40px] xl:text-[44px] font-extrabold text-[#0F172A] leading-[1.18] tracking-tight">
                  <span className="block">End-to-end Legacy Data</span>
                  <span className="block">Migration Services</span>
                </h2>
              </div>

              <div className="flex items-center gap-6 lg:max-w-[460px]">
                <div className="hidden lg:block w-[1.5px] h-16 bg-[#CBD5E1] shrink-0" />
                <p className="text-[13.5px] sm:text-[14px] leading-[22px] sm:leading-[24px] text-[#64748B]">
                  Migrate and transform legacy data into modern platforms securely and accurately, maintaining data integrity while minimizing business disruption.
                </p>
              </div>
            </div>

            {/* 6 Capabilities Cards Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {SERVICE_CAPABILITIES.map((item, idx) => {
                const IconComp = item.icon;
                return (
                  <div
                    key={idx}
                    className="rounded-[22px] bg-white p-7 sm:p-8 border border-white/80 shadow-[0_10px_30px_rgba(15,23,42,0.04)] hover:shadow-[0_20px_45px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-start"
                  >
                    <div
                      className="w-12 h-12 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: item.iconBg }}
                    >
                      <IconComp
                        className="w-5 h-5"
                        style={{ color: item.iconColor }}
                        strokeWidth={2.4}
                      />
                    </div>
                    <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                      {item.title}
                    </h3>
                    <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      {item.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* A Proven Migration Approach Section matching Screenshot */}
        <section
          className="relative w-full overflow-hidden py-16 sm:py-20 lg:py-24"
          style={{ backgroundColor: "rgba(5, 10, 21, 1)" }}
        >
          {/* Background Illustration with subtle opacity so the graphic shows softly through the dark background */}
          <div className="absolute inset-0 w-full h-full pointer-events-none select-none z-0">
            <img
              src="/images/services/legacy-migration-approach-bg.png"
              alt="A Proven Migration Approach"
              className="w-full h-full object-cover object-center pointer-events-none select-none block"
              style={{ opacity: 0.35 }}
            />
          </div>

          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            {/* Header */}
            <div className="max-w-[700px]">
              <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-white/80 select-none">
                A PROVEN MIGRATION APPROACH
              </p>
              <h2 className="mt-3 text-[30px] sm:text-[36px] lg:text-[42px] xl:text-[44px] font-extrabold text-white leading-[1.15] tracking-tight">
                <span>From Legacy to </span>
                <span className="text-[#00D2EE]">Future-Ready.</span>
              </h2>
              <p className="mt-4 text-[14px] sm:text-[15px] leading-[24px] text-[#A2B6CE] max-w-[560px]">
                Our structured approach ensures a smooth, secure, and efficient migration journey.
              </p>
            </div>

            {/* 5-Step Flow */}
            <div className="mt-14 sm:mt-16 flex flex-col lg:flex-row items-center lg:items-start justify-between gap-8 lg:gap-2">
              {MIGRATION_STEPS.map((step, idx) => {
                const IconComp = step.icon;
                const isLast = idx === MIGRATION_STEPS.length - 1;
                return (
                  <React.Fragment key={step.title}>
                    {/* Step Card */}
                    <div className="flex flex-col items-center text-center max-w-[180px] w-full group">
                      {/* Glowing Circular Icon */}
                      <div
                        className="w-16 h-16 rounded-full flex items-center justify-center transition-transform duration-300 group-hover:scale-105"
                        style={{
                          backgroundColor: step.bgColor,
                          boxShadow: `0 0 24px 3px ${step.glowColor}`,
                        }}
                      >
                        <IconComp
                          className="w-7 h-7"
                          style={{ color: step.iconColor }}
                          strokeWidth={2.3}
                        />
                      </div>

                      {/* Title */}
                      <h3 className="mt-5 text-[17px] sm:text-[18px] font-bold text-white tracking-tight">
                        {step.title}
                      </h3>

                      {/* Description */}
                      <p className="mt-2 text-[12.5px] sm:text-[13px] leading-[19px] text-[#9FB3C8]">
                        {step.desc}
                      </p>
                    </div>

                    {/* Connecting Cyan Arrow (hidden on mobile, visible on desktop) */}
                    {!isLast && (
                      <div className="hidden lg:flex items-center justify-center pt-5 shrink-0 px-1 xl:px-2">
                        <ArrowRight
                          className="w-6 h-6 text-[#00D2EE]"
                          strokeWidth={2.4}
                        />
                      </div>
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
        </section>

        {/* Transform Your Data Assets Section matching Screenshot */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-28"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10 xl:gap-16">
              {/* Left Side: Elevated Dashboard Image */}
              <div className="flex-1 w-full max-w-[680px] lg:max-w-[56%]">
                <div className="overflow-hidden rounded-[20px] sm:rounded-[24px] bg-[#071326] border border-slate-200/80 shadow-[0_20px_50px_rgba(15,23,42,0.12)]">
                  <img
                    src="/images/services/legacy-migration-dashboard.jpg"
                    alt="Simpel Data Migration Platform"
                    className="w-full h-auto object-contain block"
                  />
                </div>
              </div>

              {/* Right Side: Content */}
              <div className="flex-1 max-w-[520px]">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                  TRANSFORM YOUR DATA ASSETS
                </p>
                <h2 className="mt-3.5 text-[32px] sm:text-[38px] lg:text-[42px] xl:text-[45px] font-extrabold text-[#0F172A] leading-[1.15] tracking-tight">
                  <span className="block">Modern Platforms.</span>
                  <span className="block text-[#00D2EE]">Real Business Impact.</span>
                </h2>
                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#64748B]">
                  Our migration solutions help you move from outdated, fragmented systems to modern, integrated platforms — enabling better decisions, faster operations, and new growth opportunities.
                </p>

                {/* 3 Impact Points */}
                <div className="mt-8 sm:mt-9 flex flex-col gap-4 sm:gap-5">
                  {/* Point 1 */}
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center shrink-0">
                      <Zap className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                    </div>
                    <span className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] tracking-tight">
                      Faster Access to Information
                    </span>
                  </div>

                  {/* Point 2 */}
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center shrink-0">
                      <Database className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                    </div>
                    <span className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] tracking-tight">
                      Improved Data Quality
                    </span>
                  </div>

                  {/* Point 3 */}
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 rounded-full bg-[#EFF6FF] flex items-center justify-center shrink-0">
                      <PieChart className="w-4 h-4 text-[#2563EB]" strokeWidth={2.4} />
                    </div>
                    <span className="text-[15px] sm:text-[16px] font-bold text-[#0F172A] tracking-tight">
                      A Foundation for Analytics and AI
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Connect Across Your Ecosystem Section matching Screenshot */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-28 overflow-hidden"
          style={{ backgroundColor: "rgba(248, 250, 252, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-10 xl:gap-14">
              {/* Left Side: Text */}
              <div className="max-w-[520px] xl:max-w-[560px] shrink-0">
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
                  CONNECT ACROSS YOUR ECOSYSTEM
                </p>
                <h2 className="mt-3.5 text-[30px] sm:text-[34px] lg:text-[38px] xl:text-[42px] font-extrabold text-[#0F172A] leading-[1.18] tracking-tight">
                  <span className="block sm:whitespace-nowrap">Migrate to the Platforms</span>
                  <span className="block sm:whitespace-nowrap">
                    <span>That </span>
                    <span className="text-[#00D2EE]">Power Your Future.</span>
                  </span>
                </h2>
                <p className="mt-5 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#64748B]">
                  We support migration across a wide range of platforms and systems, tailored to your business needs.
                </p>
              </div>

              {/* Right Side: Graphic Illustration */}
              <div className="flex-1 w-full max-w-[760px] flex items-center justify-center lg:justify-end">
                <img
                  src="/images/services/legacy-migration-ecosystem.png"
                  alt="Connect across your ecosystem"
                  className="w-full h-auto object-contain block select-none pointer-events-none"
                />
              </div>
            </div>
          </div>
        </section>

        {/* Our Approach Section matching Screenshot */}
        <section
          className="relative w-full py-16 sm:py-20 lg:py-28 overflow-hidden"
          style={{ backgroundColor: "rgba(255, 255, 255, 1)" }}
        >
          <div className="mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            {/* Eyebrow */}
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.4px] text-[#64748B]">
              OUR APPROACH
            </p>

            {/* Header: Heading & Description directly aligned across from each other */}
            <div className="mt-3.5 flex flex-col lg:flex-row lg:items-center justify-between gap-6 pb-12 sm:pb-14">
              <h2 className="text-[32px] sm:text-[38px] lg:text-[42px] xl:text-[45px] font-extrabold text-[#0F172A] leading-[1.15] tracking-tight">
                <span>A clear path from opportunity </span>
                <span className="text-[#00D2EE]">to</span>
                <span className="block text-[#00D2EE]">impact.</span>
              </h2>

              <p className="text-[13.5px] sm:text-[14px] leading-[22px] sm:leading-[24px] text-[#64748B] max-w-[420px]">
                We follow a structured approach to design, deploy, and scale AI agents that deliver measurable value.
              </p>
            </div>

            {/* 4 Cards Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {APPROACH_STEPS.map((card) => {
                const IconComp = card.icon;
                return (
                  <div
                    key={card.number}
                    className="relative overflow-hidden rounded-[22px] bg-[#F8FAFC] p-7 pt-9 border border-slate-100 shadow-[0_4px_20px_rgba(15,23,42,0.03)] hover:shadow-[0_16px_36px_rgba(15,23,42,0.08)] hover:-translate-y-1 transition-all duration-300 flex flex-col justify-start"
                  >
                    {/* Top Right Number Badge */}
                    <div
                      className="absolute top-0 right-0 px-4 py-1.5 rounded-bl-[16px] text-white font-extrabold text-[13px] tracking-wider select-none"
                      style={{ background: card.badgeGradient }}
                    >
                      {card.number}
                    </div>

                    {/* Icon Circle */}
                    <div
                      className="w-11 h-11 rounded-full flex items-center justify-center mb-6"
                      style={{ backgroundColor: card.iconBg }}
                    >
                      <IconComp
                        className="w-5 h-5"
                        style={{ color: card.iconColor }}
                        strokeWidth={2.4}
                      />
                    </div>

                    {/* Title */}
                    <h3 className="text-[17px] sm:text-[18px] font-bold text-[#0F172A] leading-snug">
                      {card.title}
                    </h3>

                    {/* Description */}
                    <p className="mt-3 text-[13px] sm:text-[13.5px] leading-[21px] text-[#64748B]">
                      {card.desc}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>
        </section>

        {/* Real Solutions. Real Impact. Testimonial Section matching Agentic AI */}
        <section
          className="relative w-full overflow-hidden"
          style={{
            backgroundColor: "rgba(248, 250, 252, 1)",
          }}
        >
          <div className="relative mx-auto w-full max-w-[1440px] px-6 lg:px-[70px] py-16 sm:py-20 lg:py-[88px] flex flex-col items-center">
            {/* Eyebrow */}
            <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#64748B] text-center">
              TRUSTED BY BUSINESSES THAT INNOVATE
            </p>

            {/* Main Heading */}
            <h2 className="mt-3.5 text-[32px] sm:text-[40px] lg:text-[44px] font-extrabold leading-[1.14] tracking-tight text-center">
              <span className="text-[#0F172A]">Real Solutions. </span>
              <span className="text-[#1D70F7]">Real Impact.</span>
            </h2>

            {/* Description */}
            <p className="mt-4 text-[14px] sm:text-[15px] leading-[24px] sm:leading-[25px] text-[#556987] text-center max-w-[660px]">
              We partner with organizations across industries to modernize legacy data stacks that drive efficiency, improve decision-making, and create measurable business value.
            </p>

            {/* Testimonial Card with Prev/Next Controls */}
            <div className="mt-12 sm:mt-14 w-full flex items-center justify-center gap-3 sm:gap-6 lg:gap-8">
              {/* Prev Button */}
              <button
                onClick={handlePrevTestimonial}
                aria-label="Previous testimonial"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer shrink-0"
              >
                <ChevronLeft className="w-5 h-5" />
              </button>

              {/* Center Card */}
              <div
                className="w-full max-w-[672px] p-7 sm:p-[40px] rounded-[24px] flex flex-col gap-[15.5px] border"
                style={{
                  backgroundColor: "rgba(255, 255, 255, 1)",
                  borderColor: "rgba(226, 232, 240, 1)",
                  boxShadow: "0px 1px 2px rgba(0, 0, 0, 0.05)",
                }}
              >
                {/* Quote Icon */}
                <div className="text-[#1D70F7]">
                  <svg
                    className="w-7 h-7 sm:w-8 sm:h-8"
                    viewBox="0 0 24 24"
                    fill="currentColor"
                  >
                    <path d="M14.017 21v-7.391c0-5.704 3.731-9.57 8.983-10.609l.995 2.151c-2.432.917-3.995 3.638-3.995 5.849h4v10h-9.983zm-14.017 0v-7.391c0-5.704 3.748-9.57 9-10.609l.996 2.151c-2.433.917-3.996 3.638-3.996 5.849h3.983v10h-9.983z" />
                  </svg>
                </div>

                {/* Quote Text */}
                <p className="text-[15px] sm:text-[16.5px] leading-[26px] sm:leading-[28px] text-[#334155] italic">
                  “{TESTIMONIALS[activeTestimonial].quote}”
                </p>

                {/* Author Info */}
                <div className="mt-3 flex items-center gap-4">
                  {TESTIMONIALS[activeTestimonial].image ? (
                    <div className="w-[58px] h-[58px] sm:w-[64px] sm:h-[64px] rounded-full border-[3px] border-[#00D2EE] overflow-hidden shrink-0 shadow-sm">
                      <img
                        src={TESTIMONIALS[activeTestimonial].image}
                        alt={TESTIMONIALS[activeTestimonial].name}
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : null}
                  <div className="flex flex-col">
                    <span className="font-bold text-[19px] sm:text-[22px] text-[#0F172A] leading-tight tracking-tight">
                      {TESTIMONIALS[activeTestimonial].name}
                    </span>
                    <span className="text-[14px] sm:text-[15.5px] text-[#5A6E85] mt-1 font-medium">
                      {TESTIMONIALS[activeTestimonial].role}
                    </span>
                  </div>
                </div>
              </div>

              {/* Next Button */}
              <button
                onClick={handleNextTestimonial}
                aria-label="Next testimonial"
                className="w-10 h-10 sm:w-11 sm:h-11 rounded-full border border-slate-200 bg-white shadow-sm flex items-center justify-center text-slate-500 hover:text-slate-800 hover:border-slate-300 transition-colors cursor-pointer shrink-0"
              >
                <ChevronRight className="w-5 h-5" />
              </button>
            </div>

            {/* Dots Indicator */}
            <div className="mt-8 flex items-center justify-center gap-2">
              {TESTIMONIALS.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTestimonial(idx)}
                  aria-label={`Go to slide ${idx + 1}`}
                  className={`transition-all duration-300 rounded-full cursor-pointer ${
                    activeTestimonial === idx
                      ? "w-6 h-2 bg-[#1D70F7]"
                      : "w-2 h-2 bg-[#BFDBFE] hover:bg-[#93C5FD]"
                  }`}
                />
              ))}
            </div>
          </div>
        </section>

        {/* Ready to Move Beyond Legacy Limitations Globe CTA Section matching Screenshot */}
        <section
          id="cta"
          className="relative w-full overflow-hidden flex items-center py-20 sm:py-24 lg:py-28"
          style={{ backgroundColor: "#000E1B", minHeight: 440 }}
        >
          {/* Background globe image with BOLDER BRIGHTER TOGETHER baked in */}
          <img
            src="/images/services/agentic-ai-globe-banner.png"
            alt="Let's Build a More Connected Future Together"
            aria-hidden
            className="absolute inset-0 h-full w-full object-cover object-right lg:object-center pointer-events-none select-none"
          />

          {/* Subtle overlay on smaller screens for crisp text contrast */}
          <div className="absolute inset-0 bg-gradient-to-r from-[#000E1B]/90 via-[#000E1B]/50 to-transparent lg:hidden pointer-events-none" />

          {/* Content Container */}
          <div className="relative z-10 mx-auto w-full max-w-[1440px] px-6 lg:px-[70px]">
            <div className="flex flex-col lg:flex-row items-start justify-between gap-10">
              {/* Left Content */}
              <div className="max-w-[780px]">
                {/* Eyebrow */}
                <p className="text-[11px] sm:text-[12px] font-bold uppercase tracking-[2.5px] text-[#8EA3BD]">
                  READY TO MOVE BEYOND LEGACY LIMITATIONS?
                </p>

                {/* Heading */}
                <h2 className="mt-4 text-[28px] sm:text-[36px] lg:text-[42px] xl:text-[48px] font-extrabold text-white leading-[1.12] tracking-tight">
                  <span>Let’s Build a More </span>
                  <span className="text-[#00D2EE]">Connected</span>
                  <span className="block text-[#00D2EE]">Future Together.</span>
                </h2>

                {/* Paragraph */}
                <p className="mt-4 sm:mt-5 text-[14.5px] sm:text-[15.5px] leading-[25px] text-[#A2B6CE] max-w-[480px]">
                  Migrate, integrate, and unlock new opportunities with Simpel.
                </p>

                {/* CTA Button */}
                <div className="mt-8">
                  <button
                    onClick={handleTalkToTeam}
                    className="group inline-flex items-center gap-3 rounded-full bg-white px-7 py-3.5 text-[14.5px] font-bold text-[#04101D] transition-all duration-300 hover:scale-[1.03] active:scale-95 cursor-pointer"
                    style={{
                      boxShadow:
                        "0 0 24px 3px rgba(47, 115, 246, 0.75), 0 0 45px rgba(0, 210, 238, 0.35)",
                    }}
                  >
                    <span>Talk to Our Team</span>
                    <ArrowRight
                      className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1"
                      strokeWidth={2.2}
                    />
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default LegacyDataMigrationPage;
